import React, { useEffect, useState } from "react";
import {
  fetchSchedule, scheduleAppointment, completeBooking, updateServiceBooking,
  fetchServices, fetchResources, fetchReservations, fetchMembers, getCurrency,
  fetchServiceBookingReceipt,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Services & Memberships — scheduler / day calendar (SM5c). A column per resource
// for the chosen day; book into a slot; complete & bill via existing billing.
const S = {
  en: {
    title: "Schedule", subtitle: "Book appointments into therapists and rooms",
    newapt: "New appointment", service: "Service", resource: "Resource", none_res: "Unassigned",
    staff: "Staff", room: "Room", pick_staff: "Choose staff", pick_room: "Choose room",
    therapists: "Therapists / staff", rooms: "Rooms / places", all_areas: "All",
    time: "Date & time", bt_guest: "In-house guest", bt_member: "Member", bt_walkin: "Walk-in",
    pick_guest: "Choose guest", pick_member: "Choose member", cust_name: "Customer name",
    book: "Book", booked_ok: "Appointment booked ✓", none: "No appointments for this day.", conflict: "That slot clashes with another booking.",
    complete: "Complete", cancel: "Cancel", method: "Payment", m_cash: "Cash", m_telebirr: "telebirr", m_cbe: "CBE Birr", m_pos: "POS",
    print: "Print", receipt: "Receipt", r_subtotal: "Subtotal", r_service: "Service", r_vat: "VAT", r_total: "Total",
    b_folio: "Room bill", b_paid: "Pay now", b_membership: "Member",
    st_booked: "Booked", st_done: "Done", st_cancelled: "Cancelled", st_no_show: "No-show",
    min: "min", today: "Today", err: "Could not book.",
    options: "Options", special_request: "Special request", sr_ph: "Any request or note (optional)",
    reschedule: "Reschedule", confirm: "Confirm", new_time: "New date & time", cancel_q: "Cancel this booking?",
  },
  am: {
    title: "መርሐግብር", subtitle: "ቀጠሮዎችን ወደ ቴራፒስቶችና ክፍሎች ያስይዙ",
    newapt: "አዲስ ቀጠሮ", service: "አገልግሎት", resource: "መርጃ", none_res: "ያልተመደበ",
    staff: "ሰራተኛ", room: "ክፍል", pick_staff: "ሰራተኛ ይምረጡ", pick_room: "ክፍል ይምረጡ",
    therapists: "ቴራፒስቶች / ሰራተኞች", rooms: "ክፍሎች / ቦታዎች", all_areas: "ሁሉም",
    time: "ቀንና ሰዓት", bt_guest: "የቤት ውስጥ እንግዳ", bt_member: "አባል", bt_walkin: "መጤ ደንበኛ",
    pick_guest: "እንግዳ ይምረጡ", pick_member: "አባል ይምረጡ", cust_name: "የደንበኛ ስም",
    book: "ያዝ", none: "ለዚህ ቀን ቀጠሮ የለም።", conflict: "ይህ ሰዓት ከሌላ ማስያዣ ጋር ይጋጫል።",
    complete: "አጠናቅ", cancel: "ሰርዝ", method: "ክፍያ", m_cash: "ጥሬ ገንዘብ", m_telebirr: "ቴሌብር", m_cbe: "ሲቢኢ ብር", m_pos: "ፖስ",
    print: "አትም", receipt: "ደረሰኝ", r_subtotal: "ንዑስ ድምር", r_service: "አገልግሎት", r_vat: "ተ.እ.ታ", r_total: "ጠቅላላ",
    b_folio: "የክፍል ሂሳብ", b_paid: "አሁን ክፈል", b_membership: "አባል",
    st_booked: "ተይዟል", st_done: "ተጠናቋል", st_cancelled: "ተሰርዟል", st_no_show: "አልመጣም",
    min: "ደቂቃ", today: "ዛሬ", err: "ማስያዝ አልተሳካም።",
    options: "አማራጮች", special_request: "ልዩ ጥያቄ", sr_ph: "ማንኛውም ጥያቄ ወይም ማስታወሻ (አማራጭ)", // TODO verify am
    reschedule: "እንደገና ያዙ", confirm: "አረጋግጥ", new_time: "አዲስ ቀንና ሰዓት", cancel_q: "ይህን ማስያዣ ይሰረዝ?", // TODO verify am
  },
  om: {
    title: "Sagantaa", subtitle: "Beellama terapistootaa fi kutaawwaniif qabi",
    newapt: "Beellama haaraa", service: "Tajaajila", resource: "Qabeenya", none_res: "Kan hin ramadamne",
    staff: "Hojjetaa", room: "Kutaa", pick_staff: "Hojjetaa fili", pick_room: "Kutaa fili",
    therapists: "Terapistoota / hojjettoota", rooms: "Kutaawwan / bakkeewwan", all_areas: "Hunda",
    time: "Guyyaa fi sa'aatii", bt_guest: "Keessummaa mana keessaa", bt_member: "Miseensa", bt_walkin: "Nama dhufe",
    pick_guest: "Keessummaa fili", pick_member: "Miseensa fili", cust_name: "Maqaa maamilaa",
    book: "Qabi", none: "Guyyaa kanaaf beellamni hin jiru.", conflict: "Sa'aatiin kun beellama biraa waliin wal dhaba.",
    complete: "Xumuri", cancel: "Haqi", method: "Kaffaltii", m_cash: "Maallaqa", m_telebirr: "telebirr", m_cbe: "CBE Birr", m_pos: "POS",
    print: "Maxxansi", receipt: "Nagahee", r_subtotal: "Ida'ama xiqqaa", r_service: "Tajaajila", r_vat: "VAT", r_total: "Ida'ama",
    b_folio: "Herrega kutaa", b_paid: "Amma kaffali", b_membership: "Miseensa",
    st_booked: "Qabame", st_done: "Xumurame", st_cancelled: "Haqame", st_no_show: "Hin dhufne",
    min: "daq", today: "Har'a", err: "Qabuun hin dandeenye.",
    options: "Filannoowwan", special_request: "Gaaffii addaa", sr_ph: "Gaaffii yookaan yaadannoo kamiyyuu (filannoo)", // TODO verify om
    reschedule: "Irra deebi'ii qabi", confirm: "Mirkaneessi", new_time: "Guyyaa fi sa'aatii haaraa", cancel_q: "Qabannoo kana haquu?", // TODO verify om
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: "8px 10px", border: "1px solid #d8dee2", borderRadius: 8, fontSize: 14 };
const ST_FG = { booked: "#0C447C", done: "#1D7A44", cancelled: "#A32D2D", no_show: "#8A5A0B" };

function todayStr() { return new Date().toISOString().slice(0, 10); }
function hhmm(iso) { return iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""; }

export default function ServiceSchedulePage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const [day, setDay] = useState(todayStr());
  const [rows, setRows] = useState(null);
  const [services, setServices] = useState([]);
  const [resources, setResources] = useState([]);
  const [area, setArea] = useState("all");   // department filter (spa/gym/pool…)
  const [resList, setResList] = useState([]);
  const [memList, setMemList] = useState([]);
  const [f, setF] = useState({ service_id: "", staff_resource_id: "", room_resource_id: "", time: "", bt: "walkin", reservation_id: "", member_id: "", cust_name: "" });
  const [selItems, setSelItems] = useState([]);   // N7: chosen service options
  const [specialReq, setSpecialReq] = useState(""); // N7: free-text special request
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");   // success confirmation after booking
  const [payFor, setPayFor] = useState(null);
  const [method, setMethod] = useState("cash");
  const [reschedId, setReschedId] = useState(null);   // booking whose reschedule form is open
  const [reschedVal, setReschedVal] = useState("");   // datetime-local value

  const load = () => fetchSchedule(day).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, [day]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchServices().then((s) => setServices((s || []).filter((x) => x.active))).catch(() => {});
    fetchResources().then((r) => setResources((r || []).filter((x) => x.active))).catch(() => {});
    fetchReservations("all").then((rs) => setResList((rs || []).filter((r) => r.status === "booked" || r.status === "checked_in"))).catch(() => {});
    fetchMembers().then((m) => setMemList((m || []).filter((x) => x.active))).catch(() => {});
  }, []);

  const book = () => {
    if (!f.service_id || !f.time) return;
    if (f.bt === "guest" && !f.reservation_id) return;
    if (f.bt === "member" && !f.member_id) return;
    setBusy(true); setErr(""); setMsg("");
    const bookedDay = f.time.slice(0, 10);
    const bookedName = f.bt === "walkin" ? (f.cust_name.trim() || t("bt_walkin")) : (f.bt === "member" ? t("bt_member") : t("bt_guest"));
    const body = {
      service_id: Number(f.service_id),
      staff_resource_id: f.staff_resource_id ? Number(f.staff_resource_id) : null,
      room_resource_id: f.room_resource_id ? Number(f.room_resource_id) : null,
      scheduled_start: new Date(f.time).toISOString(), customer_type: f.bt,
      reservation_id: f.bt === "guest" ? Number(f.reservation_id) : null,
      member_id: f.bt === "member" ? Number(f.member_id) : null,
      customer_name: f.bt === "walkin" ? (f.cust_name.trim() || null) : null,
      selected_items: selItems,
      special_request: specialReq.trim() || null,
    };
    scheduleAppointment(body)
      .then(() => {
        setF({ ...f, time: "", cust_name: "", staff_resource_id: "", room_resource_id: "" }); setSelItems([]); setSpecialReq("");
        setDay(bookedDay);                 // jump the day view to the booked date so it shows
        setMsg(`${t("booked_ok")} — ${bookedName}`);
        setTimeout(() => setMsg(""), 5000);
        load();
      })
      .catch((e) => setErr((e && e.response && e.response.status === 409) ? t("conflict") : t("err")))
      .finally(() => setBusy(false));
  };
  // Reprint / print a receipt for a paid walk-in or member booking (browser
  // print — no hardware). Mirrors the ServiceBookingsPage receipt.
  const printBooking = async (b) => {
    let d;
    try { d = await fetchServiceBookingReceipt(b.id); } catch { return; }
    const m = (v) => formatPrice(v || 0, cur);
    const svcLine = (d.service_charge || 0) > 0 ? `<tr><td>${t("r_service")}</td><td class="r">${m(d.service_charge)}</td></tr>` : "";
    const vatLine = (d.vat || 0) > 0 ? `<tr><td>${t("r_vat")}${d.vat_pct ? ` ${d.vat_pct}%` : ""}</td><td class="r">${m(d.vat)}</td></tr>` : "";
    const netLine = (svcLine || vatLine) ? `<tr><td>${t("r_subtotal")}</td><td class="r">${m(d.subtotal)}</td></tr>` : "";
    const html = `<html><head><meta charset="utf-8"><title>${d.hotel || ""} — ${t("receipt")}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;max-width:360px;margin:0 auto;padding:16px;color:#111}
      h2{margin:0;font-size:17px}.muted{color:#666;font-size:11.5px}
      .head{text-align:center}.title{text-align:center;font-weight:700;font-size:14px;letter-spacing:.5px;margin:8px 0 2px}
      table{width:100%;border-collapse:collapse;margin-top:6px;font-size:12.5px}td{padding:3px 0;vertical-align:top}
      .r{text-align:right}.tot td{border-top:1px solid #999;font-weight:700;padding-top:6px}
      .hr{border-top:1px dashed #999;margin:8px 0}</style></head><body>
      <div class="head"><h2>${d.hotel || ""}</h2></div>
      <div class="title">${t("receipt")}</div>
      <div class="muted head">${d.date ? new Date(d.date).toLocaleString() : ""}</div>
      <div class="hr"></div>
      <div class="muted"><b>${d.service_name || ""}</b></div>
      <div class="muted">${d.customer_name || ""}</div>
      ${(Array.isArray(d.selected_items) && d.selected_items.length) ? `<div class="muted">${t("options")}: ${d.selected_items.join(", ")}</div>` : ""}
      ${d.special_request ? `<div class="muted">${t("special_request")}: ${d.special_request}</div>` : ""}
      <table>${netLine}${svcLine}${vatLine}
        <tr class="tot"><td>${t("r_total")}</td><td class="r">${m(d.total)}</td></tr></table>
      ${d.method ? `<div class="muted" style="margin-top:8px">${d.method}</div>` : ""}
    </body></html>`;
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) return;
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 250);
  };

  const complete = (b) => {
    if (b.billing_kind === "paid") { setPayFor(b.id); setMethod("cash"); return; }
    completeBooking(b.id, {}).then(load).catch(() => {});
  };
  const confirmPay = (b) => completeBooking(b.id, { payment_method: method }).then(() => { setPayFor(null); load(); }).catch(() => {});
  // N6 — cancel (with confirm) and reschedule (inline datetime picker; surface the
  // backend 409 conflict message if the new time clashes with another booking).
  const cancelApt = (b) => {
    if (!window.confirm(t("cancel_q"))) return;
    updateServiceBooking(b.id, { status: "cancelled" }).then(load).catch(() => {});
  };
  // Convert an ISO instant to the local "YYYY-MM-DDTHH:MM" value a
  // <input type="datetime-local"> expects (native calendar/clock picker).
  const toLocalInput = (iso) => {
    const d = iso ? new Date(iso) : new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const openReschedule = (b) => { setReschedId(b.id); setReschedVal(toLocalInput(b.scheduled_start)); };
  const closeReschedule = () => { setReschedId(null); setReschedVal(""); };
  const confirmReschedule = (b) => {
    if (!reschedVal) return;
    const when = new Date(reschedVal);
    if (isNaN(when.getTime())) { window.alert(t("err")); return; }
    updateServiceBooking(b.id, { scheduled_start: when.toISOString() })
      .then(() => { closeReschedule(); load(); })
      .catch((e) => window.alert((e && e.response && e.response.status === 409)
        ? ((e.response.data && e.response.data.detail && e.response.data.detail.message) || t("conflict"))
        : t("err")));
  };

  // group appointments by resource column
  // Department (area) filter — narrows the day view when there are many resources.
  const selService = services.find((s) => String(s.id) === String(f.service_id)) || null;
  const staffRes = resources.filter((r) => r.kind === "staff");
  const roomRes = resources.filter((r) => r.kind === "room");
  const areas = [...new Set(resources.map((r) => r.area).filter(Boolean))];
  const areaLabel = (a) => (a ? a.charAt(0).toUpperCase() + a.slice(1) : a);
  const resInArea = area === "all" ? resources : resources.filter((r) => r.area === area);
  const cols = [...resInArea.map((r) => ({ id: r.id, name: r.name, kind: r.kind })), { id: null, name: t("none_res"), kind: null }];
  const byRes = (rid) => (rows || []).filter((b) =>
    (b.resource_id || null) === rid && (area === "all" || rid !== null || (b.area || "") === area));

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {/* new appointment */}
      <div style={{ ...CARD, padding: 16, marginTop: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>{t("newapt")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          <select style={inp} value={f.service_id} onChange={(e) => { setF({ ...f, service_id: e.target.value }); setSelItems([]); }}>
            <option value="">{t("service")}</option>
            {services.map((s) => <option key={s.id} value={s.id}>{s.name}{s.duration_min ? ` (${s.duration_min}${t("min")})` : ""}</option>)}
          </select>
          {(!selService || selService.needs_staff) && (
            <select style={inp} value={f.staff_resource_id} onChange={(e) => setF({ ...f, staff_resource_id: e.target.value })}>
              <option value="">🧖 {selService && selService.staff_title ? selService.staff_title : t("staff")}</option>
              {staffRes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
          {(!selService || selService.needs_room) && (
            <select style={inp} value={f.room_resource_id} onChange={(e) => setF({ ...f, room_resource_id: e.target.value })}>
              <option value="">🚪 {t("room")}</option>
              {roomRes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
          {/* Separate date + time inputs: native pickers close on selection and
              commit immediately, unlike datetime-local (which left the value
              uncommitted, so the disabled Book button never enabled). */}
          <input style={inp} type="date" value={f.time ? f.time.slice(0, 10) : ""}
            onChange={(e) => setF({ ...f, time: e.target.value ? `${e.target.value}T${f.time ? f.time.slice(11, 16) : "09:00"}` : "" })} />
          <input style={inp} type="time" value={f.time ? f.time.slice(11, 16) : ""}
            onChange={(e) => setF({ ...f, time: `${f.time ? f.time.slice(0, 10) : todayStr()}T${e.target.value || "09:00"}` })} />
          <select style={inp} value={f.bt} onChange={(e) => setF({ ...f, bt: e.target.value })}>
            <option value="walkin">{t("bt_walkin")}</option>
            <option value="guest">{t("bt_guest")}</option>
            <option value="member">{t("bt_member")}</option>
          </select>
          {f.bt === "guest" ? (
            <select style={inp} value={f.reservation_id} onChange={(e) => setF({ ...f, reservation_id: e.target.value })}>
              <option value="">{t("pick_guest")}</option>
              {resList.map((r) => <option key={r.id} value={r.id}>{r.guest_name}{r.room_name ? ` · ${r.room_name}` : ""}</option>)}
            </select>
          ) : f.bt === "member" ? (
            <select style={inp} value={f.member_id} onChange={(e) => setF({ ...f, member_id: e.target.value })}>
              <option value="">{t("pick_member")}</option>
              {memList.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.plan_name}</option>)}
            </select>
          ) : (
            <input style={inp} placeholder={t("cust_name")} value={f.cust_name} onChange={(e) => setF({ ...f, cust_name: e.target.value })} />
          )}
        </div>
        {selService && Array.isArray(selService.options) && selService.options.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>{t("options")}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {selService.options.map((opt) => (
                <label key={opt} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={selItems.includes(opt)}
                    onChange={(e) => setSelItems((cur) => e.target.checked ? [...cur, opt] : cur.filter((x) => x !== opt))} />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        )}
        <div style={{ marginTop: 12, maxWidth: 360 }}>
          <label className="muted" style={{ fontSize: 12 }}>{t("special_request")}</label>
          <input style={{ ...inp, width: "100%" }} maxLength={500} placeholder={t("sr_ph")}
            value={specialReq} onChange={(e) => setSpecialReq(e.target.value)} />
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
          <button className="btn primary small" disabled={busy || !f.service_id || !f.time} onClick={book}>{t("book")}</button>
          {err && <span style={{ color: "#A32D2D", fontSize: 13 }}>{err}</span>}
          {msg && <span style={{ color: "#0F6E56", fontSize: 13, fontWeight: 600 }}>{msg}</span>}
        </div>
      </div>

      {/* day picker */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        <input style={inp} type="date" value={day} onChange={(e) => setDay(e.target.value)} />
        <button className="btn secondary small" onClick={() => setDay(todayStr())}>{t("today")}</button>
        {areas.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginLeft: 4 }}>
            {["all", ...areas].map((a) => (
              <button key={a} type="button" onClick={() => setArea(a)}
                style={{
                  fontSize: 12.5, padding: "5px 12px", borderRadius: 20, cursor: "pointer",
                  border: area === a ? "1.5px solid #0F6E56" : "1px solid #e2e6ea",
                  background: area === a ? "#E1F5EE" : "#fff",
                  color: area === a ? "#0F6E56" : "#5b6472", fontWeight: area === a ? 700 : 500,
                }}>
                {a === "all" ? t("all_areas") : areaLabel(a)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* day view — one column per resource */}
      {rows == null ? <p className="muted" style={{ marginTop: 16 }}>…</p> : (rows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 12, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ display: "flex", gap: 12, marginTop: 12, overflowX: "auto", paddingBottom: 8 }}>
          {cols.map((col) => {
            const apts = byRes(col.id);
            if (apts.length === 0 && col.id !== null) return null;
            return (
              <div key={String(col.id)} style={{ minWidth: 230, flex: "0 0 auto" }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#063b48", marginBottom: 8 }}>{col.kind === "room" ? "🚪 " : col.kind === "staff" ? "🧖 " : ""}{col.name}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {apts.map((b) => (
                    <div key={b.id} style={{ ...CARD, padding: "10px 12px", opacity: b.status === "cancelled" ? 0.55 : 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontWeight: 700 }}>{hhmm(b.scheduled_start)}</span>
                        <span style={{ fontSize: 11.5, color: ST_FG[b.status], fontWeight: 700 }}>{t("st_" + b.status)}</span>
                      </div>
                      <div style={{ fontSize: 13, marginTop: 2 }}>{b.service_name}{b.duration_min ? ` · ${b.duration_min}${t("min")}` : ""}</div>
                      {(b.staff_resource_name || b.room_resource_name) && (
                        <div className="muted" style={{ fontSize: 11.5 }}>
                          {b.staff_resource_name ? `🧖 ${b.staff_resource_name}` : ""}
                          {b.staff_resource_name && b.room_resource_name ? " · " : ""}
                          {b.room_resource_name ? `🚪 ${b.room_resource_name}` : ""}
                        </div>
                      )}
                      <div className="muted" style={{ fontSize: 12 }}>{b.customer_name} · {t("b_" + b.billing_kind)} · {formatPrice(b.price, cur)}</div>
                      {Array.isArray(b.selected_items) && b.selected_items.length > 0 && (
                        <div className="muted" style={{ fontSize: 11.5 }}>{t("options")}: {b.selected_items.join(", ")}</div>
                      )}
                      {b.special_request && (
                        <div className="muted" style={{ fontSize: 11.5 }}>{t("special_request")}: {b.special_request}</div>
                      )}
                      {b.status === "booked" && (
                        payFor === b.id ? (
                          <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <select style={{ ...inp, padding: "5px 8px" }} value={method} onChange={(e) => setMethod(e.target.value)}>
                              <option value="cash">{t("m_cash")}</option>
                              <option value="telebirr">{t("m_telebirr")}</option>
                              <option value="cbe_birr">{t("m_cbe")}</option>
                              <option value="pos">{t("m_pos")}</option>
                            </select>
                            <button className="btn primary small" onClick={() => confirmPay(b)}>{t("complete")}</button>
                          </div>
                        ) : reschedId === b.id ? (
                          <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <input style={{ ...inp, padding: "5px 8px" }} type="datetime-local" value={reschedVal} onChange={(e) => setReschedVal(e.target.value)} aria-label={t("new_time")} />
                            <button className="btn primary small" onClick={() => confirmReschedule(b)}>{t("confirm")}</button>
                            <button className="btn secondary small" onClick={closeReschedule}>{t("cancel")}</button>
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                            <button className="btn primary small" onClick={() => complete(b)}>{t("complete")}</button>
                            <button onClick={() => openReschedule(b)} style={{ background: "none", border: "none", color: "#0C447C", cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>{t("reschedule")}</button>
                            <button onClick={() => cancelApt(b)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>{t("cancel")}</button>
                          </div>
                        )
                      )}
                      {(b.billing_kind === "paid" || b.billing_kind === "membership") && b.status !== "cancelled" && (
                        <button className="btn secondary small" style={{ marginTop: 8 }} onClick={() => printBooking(b)}>🧾 {t("print")}</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
